import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Flatpickr from "react-flatpickr";

import BreadCrumb from "common/breadcrumb";
import LoadingComponent from "common/components/loader";

import { BrandingData } from "admin-portal/settings/branding";
import { Job, JobAllocation, JobProduct, JobService } from "models/job/job";

import { GetJobs } from "api/job";

import { UpcomingScheduledData } from "common/data";
import { JobStatuses } from "models/settings/branding";
import { AppUtility } from "utility/app-utility";
import TableContainer from "common/table-container";

const RevenueReport: React.FC = () => {
  document.title = "Revenue" + BrandingData.titleSuffix;
  const title = "Revenue Report";
  const pageTitle = "Revenue";
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState<Job[]>([]);
  const now = new Date();
  // Start of the current month
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  // End of the current month (set to the next month's first day, then subtract 1 day)
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const [selectedStDate, setSelectedStDate] = useState<Date>(startOfMonth);
  const [selectedEndDate, setSelectedEndDate] = useState<Date>(endOfMonth);
  const [totalSales, setTotalSales] = useState(0.0);
  const [totalRevenue, setTotalRevenue] = useState(0.0);

  useEffect(() => {
    loadReportData();
  }, []);
  useEffect(() => {
    loadReportData();
  }, [selectedStDate]);

  const loadReportData = async () => {
    setLoading(true);

    let _dtS =
      selectedStDate.getFullYear() +
      "-" +
      (selectedStDate.getMonth() + 1).toString().padStart(2, "0") +
      "-" +
      selectedStDate.getDate().toString().padStart(2, "0");
    let _dtEnd =
      selectedEndDate.getFullYear() +
      "-" +
      (selectedEndDate.getMonth() + 1).toString().padStart(2, "0") +
      "-" +
      selectedEndDate.getDate().toString().padStart(2, "0");

    const res = await GetJobs(
      {
        paidFrom: _dtS + " 00:00",
        paidTo: _dtEnd + " 23:59",
        jobStatuses: "5,6,7",
      },
      undefined,
      1000,
      1
    );
    if (!(res?.status?.isError ?? false) && res?.data) {
      setData(res?.data ?? []);
      setTotal(res?.totalResultCount ?? 0);
      setTotalSales(
        (res?.data ?? []).reduce((total, item) => {
          // Replace `item.sales` with the actual property holding the sales value
          return total + (item.totalAmount ?? 0);
        }, 0)
      );

      setTotalRevenue(
        (res?.data ?? []).reduce((total, item) => {
          // Replace `item.sales` with the actual property holding the sales value
          return (
            total +
            (item.totalFee ?? 0) -
            (item.totalTaxAmount ?? 0) -
            (item.totalDiscountAmount ?? 0) -
            (item.totalCost ?? 0) -
            (item.referrerAmount ?? 0)
          );
        }, 0)
      );
    } else {
      setData([]);
      setTotal(0);
      setTotalSales(0.0);
      setTotalRevenue(0.0);
    }
    setLoading(false);
  };

  const months: string[] = ["JAN", "FEB", "MAR", "APR", "JUN", "JUL", "AUG", "SEP", "NOV", "DEC"];

  const getSLineTotal = (job: Job): number => {
    let _tot = job?.totalAmount ?? 0.0;
    return _tot;
  };

  const columns = useMemo(() => {
    const baseColumns: any[] = [
      {
        header: "Date",
        accessorKey: "date",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const _dt: string = cell.row.original.paidDate || cell.row.original.insertedTime;
          return (
            <>
              <p className=""> {_dt.split("T")[0]}</p>
            </>
          );
        },
      },
      {
        header: "Job",
        accessorKey: "description",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const _invC = (cell.row.original.invoices ?? []).length;
          return (
            <>
              <p className=""> {cell.row.original.vehicle.vehicleNo}</p>
              <p className=""> {cell.row.original.description}</p>
              <p className=""> {_invC > 0 && cell.row.original.invoices[_invC - 1].invoiceReference}</p>
            </>
          );
        },
      },
      {
        header: `Price`,
        accessorKey: "price",
        headerStyle: { textAlign: "right" },
        headerAlign: "right",
        enableColumnFilter: false,
        cell: (cell: any) => {
          // Format the fee to 2 decimal places with commas
          const formattedTotal = cell.row.original.totalFee
            ? new Intl.NumberFormat().format(cell.row.original.totalFee.toFixed(2))
            : "0.00"; // Fallback to "0.00" if no value exists
          const formattedTotalTax = cell.row.original.totalTaxAmount
            ? new Intl.NumberFormat().format(cell.row.original.totalTaxAmount.toFixed(2))
            : "0.00"; // Fallback to "0.00" if no value exists
          return (
            <>
              <p className="text-right"> {formattedTotal}</p>
              {cell.row.original.totalTaxAmount > 0 && <p className="text-right"> Tax( {formattedTotalTax})</p>}
            </>
          );
        },
      },
      {
        header: "Discount",
        accessorKey: "discount",
        headerStyle: { textAlign: "right" },
        headerAlign: "right",
        enableColumnFilter: false,
        cell: (cell: any) => {
          // Format the fee to 2 decimal places with commas
          const formattedTotal = cell.row.original.totalDiscountAmount
            ? new Intl.NumberFormat().format(cell.row.original.totalDiscountAmount.toFixed(2))
            : "0.00"; // Fallback to "0.00" if no value exists
          return (
            <>
              <p className="text-right"> {formattedTotal}</p>
            </>
          );
        },
      },
      {
        header: "Cost",
        accessorKey: "cost",
        headerStyle: { textAlign: "right" },
        enableColumnFilter: false,
        cell: (cell: any) => {
          // Format the fee to 2 decimal places with commas
          let _cost = 0.0;
          if (cell.row.original.totalCost != null) {
            _cost = cell.row.original.totalCost ?? 0.0;
          }
          if (cell.row.original.referrerAmount != null) {
            _cost += cell.row.original.referrerAmount ?? 0.0;
          }
          const formattedTotal = _cost ? new Intl.NumberFormat().format(_cost) : "0.00"; // Fallback to "0.00" if no value exists
          return (
            <>
              <p className="text-right"> {formattedTotal}</p>
            </>
          );
        },
      },
      {
        header: "Revenue",
        accessorKey: "revenue",
        headerAlign: "right",
        headerStyle: { textAlign: "right" },
        enableColumnFilter: false,
        cell: (cell: any) => {
          const revenue =
            (cell.row.original.totalFee ?? 0) -
            // (cell.row.original.totalTaxAmount ?? 0) -
            (cell.row.original.totalDiscountAmount ?? 0) -
            (cell.row.original.referrerAmount ?? 0) -
            (cell.row.original.totalCost ?? 0);
          // Format the fee to 2 decimal places with commas
          const formattedTotal = revenue ? new Intl.NumberFormat().format(revenue) : "0.00"; // Fallback to "0.00" if no value exists
          return (
            <>
              <p className="text-right"> {formattedTotal}</p>
            </>
          );
        },
      },
    ];

    return baseColumns;
  }, []);

  return (
    <React.Fragment>
      <BreadCrumb title={title} pageTitle={pageTitle} />
      <div className="min-w-[50%]">
        <div className="card">
          <div className="card-body">
            <div className="flex flex-col gap-4 mt-3">
              <div className="flex flex-col gap-3">
                <div className="flex flex-row">
                  <div>
                    <label htmlFor="startDate" className="inline-block mb-2 text-base font-medium p-2">
                      Sales From
                    </label>
                  </div>
                  <div>
                    <Flatpickr
                      id="startDate"
                      className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 `}
                      options={{
                        dateFormat: "d M, Y",
                        locale: "default",
                      }}
                      placeholder="Select Start Date"
                      onChange={(date) => {
                        setSelectedStDate(date[0]);
                      }}
                      value={selectedStDate ?? ""}
                    />
                  </div>
                  <div>
                    <label htmlFor="endDate" className="inline-block mb-2 text-base font-medium p-2">
                      To
                    </label>
                  </div>
                  <div>
                    <Flatpickr
                      id="endDate"
                      className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 `}
                      options={{
                        dateFormat: "d M, Y",
                        locale: "default",
                      }}
                      placeholder="Select End Date"
                      onChange={(date) => {
                        setSelectedEndDate(date[0]);
                      }}
                      value={selectedEndDate ?? ""}
                    />
                  </div>
                </div>
                <div className="flex flex-row">{loading && <LoadingComponent />}</div>
              </div>

              {!loading && (
                <>
                  <div className="overflow-x-auto mt-2">
                    {data && data.length > 0 ? (
                      <TableContainer
                        isPagination={false}
                        columns={columns || []}
                        data={data || []}
                        totalCount={total}
                        customPageSize={total}
                        divclassName="overflow-x-auto"
                        tableclassName="w-full whitespace-nowrap"
                        theadclassName="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:bg-zink-600 dark:text-zink-200"
                        thclassName="px-3.5 py-2.5 font-semibold border-b border-slate-200 dark:border-zink-500"
                        tdclassName="px-3.5 py-2.5 border-y border-slate-200 dark:border-zink-500"
                        PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
                      />
                    ) : (
                      <div className="noresult">
                        <div className="py-6 text-center">
                          {/*   <Search className="size-6 mx-auto text-sky-500 fill-sky-100 dark:sky-500/20" /> */}
                          <h5 className="mt-2 mb-1">Sorry! No Result Found</h5>
                          <p className="mb-0 text-slate-500 dark:text-zink-200">
                            No sales for the selected date range.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/*   {(data || []).map((item: Job, key: number) => {
                const _currentDt = new Date();
                _currentDt.setHours(0);
                _currentDt.setMinutes(0);
                _currentDt.setSeconds(0);
                const _dt =
                  (item.paidDate ?? item.insertedTime ?? "") != ""
                    ? new Date(item.paidDate ?? item.insertedTime ?? "")
                    : _currentDt;
                return (
                  <>
                    <div className="flex gap-3 bg-gray-100">
                      <div className="flex flex-col items-center justify-center bg-gray-100 size-12 border rounded-sm border-slate-200 dark:border-zink-500 shrink-0">
                        <h6>
                          {_dt.getDate().toString().padStart(2, "0")}
                        </h6>
                        <span className="text-sm text-slate-500 dark:text-zink-200">{months[_dt.getMonth()]}</span>
                      </div>
                      <div className="flex flex-col items-center  justify-start px-4 pt-4">
                        <h6>{item?.vehicle?.vehicleNo ?? ""}</h6>
                        <span className="text-sm text-slate-500 dark:text-zink-200"></span>
                      </div>
                      <div className="grow">
                        <h6 className="mb-1 text-slate-500 dark:text-zink-200">
                          {item.description ?? ""}{" "}
                        
                        </h6>
                        <p className="text-slate-500 dark:text-zink-200"></p>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className="w-full">
                        <div className="rounded-lg border border-gray-300 p-4 shadow-md">
                       
                          <div className="grid grid-cols-1 gap-x-5 mt-4">
                            {(item.services ?? []).map((service: JobService, index) => (
                              <div key={index} className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-2 items-center">
                                <span className="lg:col-span-6">
                                  {((service.description ?? service.service?.name ?? "") + " <M>").replace(
                                    /<.*?>/g,
                                    ""
                                  )}
                                </span>
                                <div className="lg:col-span-6">
                                  <label
                                    htmlFor={`price_tax_${index}`}
                                    className="inline-block m-4  text-sm text-right"
                                  >
                                    {(service.totalAmount ?? 0).toFixed(2)}
                                  </label>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="grid grid-cols-1 gap-x-5 mt-4">
                            {(item.products ?? []).map((prd: JobProduct, index) => (
                              <div key={index} className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-2 items-center">
                                <span className="lg:col-span-6">
                                  {(prd.description ?? prd.product?.name ?? "").replace(/[<>]/g, "")}
                                </span>

                                <div className="lg:col-span-6">
                                  <label
                                    htmlFor={`price_tax_${index}`}
                                    className="inline-block m-4  text-sm text-right"
                                  >
                                    {(prd.totalAmount ?? 0).toFixed(2)}
                                  </label>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-5 font-bold">
                          Total : {AppUtility.FormattedPrice(item.totalAmount ?? 0.0)}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className="border-t border-gray-300 w-full h-0"> </div>
                    </div>
                  </>
                );
              })} */}
            </div>
            {!loading && (
              <>
                <div className="flex gap-3 w-full pt-2">
                  <div className="flex justify-end font-bold text-m w-full">
                    Total Sales: {AppUtility.FormattedPrice(totalSales)}
                  </div>
                </div>
                <div className="flex gap-3 w-full pt-4">
                  <div className="flex justify-end font-bold text-m w-full">
                    Total Revenue: {AppUtility.FormattedPrice(totalRevenue)}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default RevenueReport;
